import {getAttemptScores} from '../utils/examScores';

const validId = id => typeof id === 'string' && id.trim().length > 0 && id.length <= 1500 && !id.includes('/');
const count = value => Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0;
const messageOf = (error, fallback) => typeof error?.message === 'string' && error.message.trim()
    ? error.message.trim().slice(0, 600) : fallback;

export function getBulkEligibility(attempt, mode) {
    try {
        if (!attempt || !validId(attempt.id)) return {eligible: false, reason: 'This submission is unavailable. Refresh the submissions list.'};
        if (mode === 'revaluation') return attempt.resubmissionStatus === 'revaluation_requested'
            ? {eligible: true, reason: ''}
            : {eligible: false, reason: 'There is no pending revaluation request for this submission.'};
        if (mode === 'pending') {
            const scores = getAttemptScores(attempt);
            return scores.hasPending || scores.pendingCount > 0
                ? {eligible: true, reason: ''}
                : {eligible: false, reason: 'This submission already has confirmed marks.'};
        }
        return {eligible: false, reason: 'Choose revaluation approval or pending evaluation.'};
    } catch {
        return {eligible: false, reason: 'This submission could not be checked. Refresh before trying again.'};
    }
}

/**
 * Processes one saved submission at a time. reviewAttempt must re-read the
 * submission, enforce access/version checks, and resolve only after saving it.
 * The observer receives copies; neither it nor delayed progress can mutate an
 * already completed outcome. Stopping lets the active save finish normally.
 */
export async function runBulkEvaluation({attempts = [], mode, reviewAttempt, onUpdate, shouldStop} = {}) {
    const queue = [], seen = new Set();
    const items = Array.isArray(attempts) ? attempts : [];
    const reservedIds = new Set(items.map(attempt => attempt?.id).filter(validId));
    items.forEach((attempt, index) => {
        const sourceId = attempt?.id;
        if (validId(sourceId) && seen.has(sourceId)) return;
        let id = sourceId;
        if (!validId(id)) {
            id = `unavailable-submission-${index + 1}`;
            while (reservedIds.has(id) || seen.has(id)) id += '-invalid';
        }
        seen.add(id);
        const eligibility = getBulkEligibility(attempt, mode);
        queue.push({attempt, record: {
            id, status: eligibility.eligible ? 'queued' : 'skipped', completed: 0, total: 0,
            pendingCount: 0, message: eligibility.reason,
        }});
    });

    const snapshot = () => queue.map(({record}) => ({...record}));
    const publish = () => {
        // Losing a progress view must not interrupt a confirmed result save.
        try { onUpdate?.(snapshot()); } catch { /* The caller can read final outcomes. */ }
    };
    const stopRequested = () => {
        try { return Boolean(shouldStop?.()); }
        catch { return true; }
    };
    publish();
    for (const item of queue) {
        const {attempt, record} = item;
        if (record.status !== 'queued') continue;
        if (stopRequested()) {
            queue.forEach(entry => {
                if (entry.record.status === 'queued') Object.assign(entry.record, {
                    status: 'cancelled', message: 'Stopped before this submission was processed.',
                });
            });
            publish();
            break;
        }
        // UI data can change while previous submissions are being marked. The
        // supplied reviewAttempt performs the authoritative server re-check too.
        const eligibility = getBulkEligibility(attempt, mode);
        if (!eligibility.eligible) {
            Object.assign(record, {status: 'skipped', message: eligibility.reason});
            publish();
            continue;
        }
        Object.assign(record, {status: 'running', message: 'Preparing saved answers…'});
        publish();
        let acceptingProgress = true;
        try {
            if (typeof reviewAttempt !== 'function') throw new Error('Evaluation could not start. Refresh the submissions list and try again.');
            const result = await reviewAttempt(attempt, mode, progress => {
                if (!acceptingProgress || record.status !== 'running') return;
                const total = count(progress?.total), completed = Math.min(count(progress?.completed), total);
                Object.assign(record, {completed, total, message: total ? `Evaluated ${completed} of ${total} answers…` : 'Preparing saved answers…'});
                publish();
            });
            acceptingProgress = false;
            if (!Number.isFinite(result?.pendingCount) || result.pendingCount < 0 || !Number.isInteger(result.pendingCount)) {
                throw new Error('The updated result could not be confirmed. Refresh this submission before retrying.');
            }
            const pendingCount = result.pendingCount;
            Object.assign(record, {
                status: pendingCount ? 'needs-review' : 'completed', completed: record.total,
                pendingCount,
                message: pendingCount
                    ? `Confirmed marks saved. ${pendingCount} answer(s) still need review.`
                    : mode === 'revaluation' ? 'Revaluation approved and saved.' : 'Pending evaluation completed and saved.',
            });
        } catch (error) {
            const skipped = error?.code === 'bulk-skipped';
            Object.assign(record, {
                status: skipped ? 'skipped' : 'failed',
                message: messageOf(error, skipped ? 'This submission is no longer eligible.' : 'This submission could not be evaluated. Its saved marks have been kept.'),
            });
        } finally {
            acceptingProgress = false;
        }
        publish();
    }
    return snapshot();
}
